import { BehaviorSubject, Observable } from 'rxjs';
import {
  ConfigurationId,
  PerformanceConfig,
  PerformanceType,
  PerformanceViewOption,
  PPSSState,
  ProductData,
} from '@types';

import ppssInternationalCoreQuery from '@graphql/fund-listing/international-core.graphql';
import ppssCommonPerformanceQuery from '@graphql/fund-listing/common-performance.graphql';
import ppssAvAnnualMonthlyQuery from '@graphql/fund-listing/av-annual-monthly.graphql';
import ppssAvAnnualQuarterlyQuery from '@graphql/fund-listing/av-annual-quarterly.graphql';
import ppssCalendarYearMonthlyQuery from '@graphql/fund-listing/calendar-year-monthly.graphql';
import ppssCalendarYearQuarterlyQuery from '@graphql/fund-listing/calendar-year-quarterly.graphql';
import ppssCumulativeMonthlyQuery from '@graphql/fund-listing/cumulative-monthly.graphql';
import ppssCumulativeQuarterlyQuery from '@graphql/fund-listing/cumulative-quarterly.graphql';
import ppssDiscreteMonthlyQuery from '@graphql/fund-listing/discrete-monthly.graphql';
import ppssDiscreteQuarterlyQuery from '@graphql/fund-listing/discrete-quarterly.graphql';
import ppssInternationalRatingsQuery from '@graphql/fund-listing/international-ratings.graphql';
import ppssInternationalIdentifiersQuery from '@graphql/fund-listing/international-identifiers.graphql';

import ppssUsCoreQuery from '@graphql/fund-listing/us-core.graphql';
import ppssUsIndentifiers from '@graphql/fund-listing/us-identifiers.graphql';
import ppssUsRatingsQuery from '@graphql/fund-listing/us-ratings.graphql';
import ppssUsYieldsQuery from '@graphql/fund-listing/us-yields.graphql';

import ppssUs529Query from '@graphql/fund-listing/us-529.graphql';
import ppssUsCefQuery from '@graphql/fund-listing/us-cef.graphql';
import ppssUsSmaQuery from '@graphql/fund-listing/us-sma.graphql';
import ppssUsVipQuery from '@graphql/fund-listing/us-vip.graphql';
import { FundFilterService } from './fund-filter.service';
import { PagedRecords } from '@utils/paging/paging-utils';
import { Product } from '@models';
import { SiteConfigService } from '@services/site-config.service';
import kebabCase from 'lodash/kebabCase';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { Logger } from '@utils/logger';
import { DocumentNode } from 'graphql';
import { SegmentService } from '@services/segment.service';
import { DebugService } from '@services/debug.service';

const logger = Logger.getLogger('BasePpssService');
const QUERIES = {
  DISCRETE_MONTHLY: ppssDiscreteMonthlyQuery,
  DISCRETE_QUARTERLY: ppssDiscreteQuarterlyQuery,
  CUMULATIVE_MONTHLY: ppssCumulativeMonthlyQuery,
  CUMULATIVE_QUARTERLY: ppssCumulativeQuarterlyQuery,
  CALENDAR_YEAR_MONTHLY: ppssCalendarYearMonthlyQuery,
  CALENDAR_YEAR_QUARTERLY: ppssCalendarYearQuarterlyQuery,
  AVG_ANNUAL_MONTHLY: ppssAvAnnualMonthlyQuery,
  AVG_ANNUAL_QUARTERLY: ppssAvAnnualQuarterlyQuery,
};

/**
 * Common functionality for all PPSS components
 */
export class BasePpssService {
  private productType: ConfigurationId;
  private subject;
  protected pagedFunds: PagedRecords<ProductData>;
  isSiteIntl: boolean;
  _isFilterProcessing = false;

  constructor(
    productType: ConfigurationId,
    protected state: PPSSState,
    protected filterService: FundFilterService,
    protected siteConfigService: SiteConfigService,
    segmentService: SegmentService,
    protected debugService: DebugService
  ) {
    this.productType = productType;
    this.subject = new BehaviorSubject(this.state);
    this.pagedFunds = new PagedRecords<ProductData>(25); // TODO: use const/config for pageSize
    this.isSiteIntl = this.siteConfigService.product?.site?.country !== 'US'; // Set the site international flag value accordingly.
    this.filterService.getData().subscribe(([dataType, data]) => {
      if (dataType === 'filteredProducts') {
        this.filterProducts(data);
      }
    });
    segmentService.getCurrentSegmentId$().subscribe(() => {
      this.state.data.tableRows = [];
      this.state.data.filteredTableRows = [];
      this.state.hasLoadedFullFeed = false;
    });
    this.debugService
      .getExpandAllComponents$()
      .subscribe((expandAll: boolean): void => {
        if (expandAll) {
          this.pagedFunds.expandAll();
        }
      });
  }

  public get isFilterProcessing(): boolean {
    return this._isFilterProcessing;
  }

  filterProducts(filteredProducts: ProductData[]) {
    this._isFilterProcessing = true;
    this.state.data.filteredTableRows = filteredProducts;
    this.pagedFunds.records = filteredProducts;
    this.sendNext('filterRows');
    this._isFilterProcessing = false;
  }

  goToPage(n: number): void {
    if (this.pagedFunds) {
      this.pagedFunds.pageNumber = n;
    }
  }

  getNumberOfPages$(): Observable<number> {
    return this.pagedFunds.numberOfPages$;
  }

  getPageNumber$(): Observable<number> {
    return this.pagedFunds.pageNumber$;
  }

  getCurrentPage$(): Observable<ProductData[]> {
    return this.pagedFunds.currentPage$;
  }

  /**
   * send update to observers
   * @param actionName - to give guidance to Observers as to the nature of the change
   */
  sendNext(actionName: string) {
    this.state.lastAction = actionName;
    this.subject.next(this.state);
  }

  subscribe(subscriber) {
    return this.subject.subscribe(subscriber);
  }

  getProductType(): ConfigurationId {
    return this.productType;
  }

  getProductTypeParameter(
    configurationId: ConfigurationId
  ): ProductTypeParameter {
    switch (configurationId) {
      case ConfigurationId.MF:
        return ProductTypeParameter.MUTUAL_FUNDS;
      case ConfigurationId.MMF:
        return ProductTypeParameter.MONEY_MARKET_FUNDS;
      case ConfigurationId.ETF:
        return ProductTypeParameter.ETFS;
      case ConfigurationId.CEF:
        return ProductTypeParameter.CLOSED_END_FUNDS;
      case ConfigurationId.TIF:
        return ProductTypeParameter.TIFs;
      case ConfigurationId.VIP:
        return ProductTypeParameter.VARIABLE_INSURANCES;
      case ConfigurationId._529:
        return ProductTypeParameter.PORTFOLIOS_529;
      case ConfigurationId.SMA:
        return ProductTypeParameter.SMAs;
      default:
        logger.error('unknown product type: ', configurationId);
    }
    throw Error('unknown product type: ' + configurationId);
  }
  /**
   * Set the nav change value to the state.
   * @param selectedCalcType the switch value. Ex. nav/pop/market_price
   */
  changeCalcTab(selectedCalcType: string) {
    this.state.config.calcType = selectedCalcType;
    this.sendNext('changeCalcTab');
  }

  /**
   * Set the performance details change to the state.
   * @param view performance view
   * @param periodEnd selected period end
   * @param performanceCurrency selected performance currency
   */
  changePerformanceView(
    view: PerformanceViewOption,
    periodEnd: string,
    performanceCurrency: string
  ) {
    this.state.config.performanceView = view;
    this.state.config.performanceViewOptions.forEach(
      (performanceViewOption: PerformanceViewOption) => {
        performanceViewOption.isDefault =
          performanceViewOption.name === view.name;
      }
    );
    this.state.config.periodEnd = periodEnd;
    this.state.config.selectedPerformanceCurrencyOption = performanceCurrency;
    this.sendNext('performanceView');
  }

  /**
   * Flatten the productsData against their sharedClasses.
   * @param products ppss data from feed
   */
  getProductsData(products: Product[]): ProductData[] {
    return this.sortProductsData(
      products
        .map((product) => {
          return product.shareClasses.map((shareclass) => {
            const { shareClasses, ...productRef } = product;
            const productData: ProductData = productRef;
            productData.shareClass = shareclass;
            return productData;
          });
        })
        .flat()
    );
  }

  /**
   * DEFECT-FIX: NGC-3045.
   * Sort the fund listing based upon full fund name column.
   * UPDATED-FIX: NGC-8947.
   * @param products fund listing
   */
  sortProductsData(products: ProductData[]): ProductData[] {
    if (this.productType === ConfigurationId._529) {
      products = products.sort((current, next) => {
        if (current.fundNameForSorting !== next.fundNameForSorting) {
          return this.sortAlphaNum(
            current.fundNameForSorting,
            next.fundNameForSorting
          );
        } else {
          return this.sortAlphaNum(
            current.shareClass.shareClassName,
            next.shareClass.shareClassName
          );
        }
      });
      return products;
    } else if (this.productType === ConfigurationId.SMA) {
      products.sort((a, b) => {
        // to apply custom sort when string contains 'number-number' pattern
        if (a.fundName.match(/\d+-\d+/g) || b.fundName.match(/\d+-\d+/g)) {
          const fundNameCompVal = this.sortAlphaNum(a.fundName, b.fundName);
          return fundNameCompVal;
        } else {
          const fundNameCompVal = a.fundName.localeCompare(b.fundName);

          if (fundNameCompVal === 0) {
            return a.shareClass.shareClassName.localeCompare(
              b.shareClass.shareClassName
            );
          }

          return fundNameCompVal;
        }
      });
      return products;
    } else {
      products.sort((a, b) => {
        const fundNameCompVal = a.fundName.localeCompare(b.fundName);

        if (fundNameCompVal === 0) {
          return a.shareClass.shareClassName.localeCompare(
            b.shareClass.shareClassName
          );
        }

        return fundNameCompVal;
      });
      return products;
    }
  }

  sortByFavFund(products: ProductData[]): ProductData[] {
    return products.sort((a, b) => {
      return a.isFavoriteFund === b.isFavoriteFund
        ? 0
        : a.isFavoriteFund
        ? -1
        : 1;
    });
  }

  /**
   * DEFECT-FIX: NGC-8947.
   * Sort the full fund name column with alphanumeric values.
   * @param products fund listing
   */
  sortAlphaNum(a: string, b: string): number {
    const reA = /[^a-zA-Z]/g;
    const reN = /[^0-9]/g;
    const aA = a.replace(reA, '');
    const bA = b.replace(reA, '');
    if (aA === bA) {
      const aN = parseInt(a.replace(reN, ''), 10);
      const bN = parseInt(b.replace(reN, ''), 10);
      return aN === bN ? 0 : aN > bN ? 1 : -1;
    } else {
      return aA > bA ? 1 : -1;
    }
  }

  /**
   * Create performance view option array.
   * @param performanceConfig ppss component configuration
   */
  createViewOptions(
    performanceConfig: PerformanceConfig
  ): PerformanceViewOption[] {
    const dataRefreshRate =
      performanceConfig.dataRefreshRate[this.getProductType()];
    const performanceViewConfigs: PerformanceType[] =
      performanceConfig.ppssPerformanceViews[this.getProductType()];

    const performanceViewOptions: PerformanceViewOption[] = performanceViewConfigs.map(
      (perfType) => ({
        name: perfType,
        labelKey: `products.perf-view-${kebabCase(perfType)}`,
        headerLabelKey: `products.perf-view-header-${kebabCase(perfType)}`,
        periodEnds: this.siteConfigService.decodePeriodEnds(
          dataRefreshRate[perfType]
        ),
        isDefault: false,
      })
    );

    if (performanceViewOptions.length > 0) {
      performanceViewOptions[0].isDefault = true; // set a default
    }
    return performanceViewOptions;
  }

  /**
   * Fetch the appropriate query as per decorated config.
   */
  fetchQueries(): DocumentNode[] {
    if (this.isSiteIntl) {
      switch (this.getProductType()) {
        case ConfigurationId.MF: // fall through
        case ConfigurationId.MMF: // fall through
        case ConfigurationId.ETF:
          const ret = [
            ppssInternationalCoreQuery,
            ppssInternationalIdentifiersQuery,
            ppssCommonPerformanceQuery,
            ppssInternationalRatingsQuery,
          ];
          this.state.config.performanceViewOptions.forEach((option) => {
            option.periodEnds.forEach((period) => {
              ret.push(QUERIES[`${option.name}_${period}`]);
            });
          });
          return ret;
      }
    } else {
      switch (this.getProductType()) {
        case ConfigurationId.MF:
        case ConfigurationId.ETF: // fall through
        case ConfigurationId.MMF: // fall through
        case ConfigurationId.TIF:
          return [
            ppssUsCoreQuery,
            ppssUsIndentifiers,
            ppssCommonPerformanceQuery,
            ppssAvAnnualMonthlyQuery,
            ppssAvAnnualQuarterlyQuery,
            ppssUsYieldsQuery,
            ppssUsRatingsQuery,
          ];
        case ConfigurationId.CEF:
          return [ppssUsCefQuery];
        case ConfigurationId.VIP:
          return [ppssUsVipQuery];
        case ConfigurationId._529:
          return [ppssUs529Query];
        case ConfigurationId.SMA:
          return [ppssUsSmaQuery];
      }
    }
  }
}
